"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { AddTypeOffcanvas } from "./add"
import { EditTypeOffcanvas } from "./edit"
import { TypesList, type TypeFilters } from "./list"
import api from "../../../libs/axios"
import { TrainingType, Pagination } from "../types"
import { useDebounce } from "../../../hooks/useDebounce"

interface TypeFormData {
  training_name: string
  training_description?: string
  validity_months?: number
}

interface EditTypeFormData extends TypeFormData {
  id: number
}

export const TrainingTypesManagement: React.FC = () => {
  const [types, setTypes] = useState<TrainingType[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0, limit: 10, page: 1, total_pages: 1, total_count: 0,
    has_next_page: false, has_prev_page: false, next_page: null, prev_page: null,
  })

  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [selectedType, setSelectedType] = useState<TrainingType | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [advancedFilters, setAdvancedFilters] = useState<TypeFilters>({})

  const fetchTypes = async (
    search = "",
    status = "",
    page = 1,
    limitValue?: number,
    sort = sortConfig,
    filters = advancedFilters,
  ) => {
    setLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10
      const queryParams: any = { page, limit: effectiveLimit }
      if (search.trim()) queryParams.search = search
      if (status) queryParams.is_active = status
      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          queryParams[key] = value
        }
      })

      const response = await api.get("/training/courses/list", { params: queryParams })
      if (response.data.success) {
        setTypes(response.data.data.training_types || [])
        setPagination(response.data.data.pagination || pagination)
      }
    } catch (error) {
      console.error("Error fetching training types:", error)
    } finally {
      setLoading(false)
    }
  }

  const createType = async (data: TypeFormData) => {
    setActionLoading(true)
    try {
      const response = await api.post("/training/courses/create", data)
      if (response.data.success) {
        await fetchTypes(searchTerm, statusFilter, 1, undefined, sortConfig, advancedFilters)
        setShowAdd(false)
      }
    } catch (error) {
      console.error("Error creating training type:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateType = async (data: EditTypeFormData) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/training/courses/${data.id}`, data)
      if (response.data.success) {
        await fetchTypes(searchTerm, statusFilter, pagination.page, undefined, sortConfig, advancedFilters)
        setShowEdit(false)
        setSelectedType(null)
      }
    } catch (error) {
      console.error("Error updating training type:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const toggleStatus = async (type: TrainingType) => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/training/courses/${type.id}/status`, { is_active: !type.is_active })
      if (response.data.success) {
        await fetchTypes(searchTerm, statusFilter, pagination.page, undefined, sortConfig, advancedFilters)
      }
    } catch (error) {
      console.error("Error toggling type status:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleSearch = (search: string) => { setSearchTerm(search) }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchTypes(debouncedSearch, statusFilter, 1, undefined, sortConfig, advancedFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])
  const handleStatusFilter = (status: string) => { setStatusFilter(status); fetchTypes(searchTerm, status, 1, undefined, sortConfig, advancedFilters) }
  const handlePageChange = (page: number) => { fetchTypes(searchTerm, statusFilter, page, undefined, sortConfig, advancedFilters) }
  const handleLimitChange = (limit: number) => { setPagination(prev => ({ ...prev, limit, page: 1 })); fetchTypes(searchTerm, statusFilter, 1, limit, sortConfig, advancedFilters) }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchTypes(searchTerm, statusFilter, pagination.page, undefined, sort, advancedFilters)
  }

  const handleAdvancedFilter = (filters: TypeFilters) => {
    setAdvancedFilters(filters)
    fetchTypes(searchTerm, statusFilter, 1, undefined, sortConfig, filters)
  }

  const handleEdit = (type: TrainingType) => { setSelectedType(type); setShowEdit(true) }
  const handleCloseModals = () => { setShowAdd(false); setShowEdit(false); setSelectedType(null) }

  useEffect(() => { fetchTypes() }, [])

  return (
    <div>
      <TypesList
        types={types} pagination={pagination} onSearch={handleSearch} onStatusFilter={handleStatusFilter}
        onPageChange={handlePageChange} onLimitChange={handleLimitChange} onAdd={() => setShowAdd(true)}
        onEdit={handleEdit} onToggleStatus={toggleStatus} loading={loading} sortConfig={sortConfig} onSort={handleSort}
        onAdvancedFilter={handleAdvancedFilter} advancedFilters={advancedFilters}
      />
      <AddTypeOffcanvas isOpen={showAdd} onClose={handleCloseModals} onSubmit={createType} loading={actionLoading} />
      <EditTypeOffcanvas isOpen={showEdit} onClose={handleCloseModals} onSubmit={updateType} trainingType={selectedType} loading={actionLoading} />
    </div>
  )
}
